import clsx from "clsx";
import { X } from "lucide-react";
import { useTranslations } from "next-intl";
import { ChangeEvent, useState } from "react";
import Button from "../../Button/Button";

type Option = {
  label: string;
  value: string;
};

type Props = {
  onClose: () => void;
  title?: string;
  options: Option[];
  value: string;
  name: string;
  onChange: (value: string) => void;
  isFullScreenModal: boolean;
};

export const Dropdown = ({
  options,
  onClose,
  title,
  value,
  name,
  onChange,
  isFullScreenModal,
}: Props) => {
  const [temporaryValue, setTemporaryValue] = useState(value);
  const t = useTranslations();

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (isFullScreenModal) {
      setTemporaryValue(event.currentTarget.value);
      return;
    }

    onChange(event.currentTarget.value);
    onClose();
  };

  const onConfirm = () => {
    onChange(temporaryValue);
    onClose();
  };

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 bg-black/35 z-20 md:static md:bg-transparent">
      <div
        className={clsx(
          "z-20 bg-lightwhite ",
          isFullScreenModal
            ? "fixed top-0 left-0 right-0 bottom-0 dark:bg-sleap-gray-950"
            : "fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 md:absolute md:top-full md:left-0 md:translate-x-0 md:translate-y-0 min-w-56 rounded-2xl shadow-[0_23px_45px_-10px_rgba(0,0,0,0.25)] px-2 py-3 dark:bg-sleap-gray-850 md:p-2 md:dark:bg-sleap-gray-950 dark:border-sleap-gray-700 dark:border"
        )}
      >
        {isFullScreenModal && (
          <div className="py-5 px-4 flex justify-between gap-2">
            <h3 className="font-inter text-lg font-medium">{title}</h3>
            <button
              type="button"
              className="p-2 bg-sleap-gray-200 dark:bg-sleap-gray-600"
              onClick={onClose}
            >
              <X />
            </button>
          </div>
        )}
        <ul>
          {options.map((option) => {
            const isChecked = option.value === temporaryValue;

            return (
              <li key={option.value} className="w-full">
                <label className="relative inline-flex flex-row-reverse gap-3 px-4 py-3 cursor-pointer text-base font-medium select-none items-center text-sleap-gray-700 dark:text-white w-full">
                  <span className="inline-block w-full">{option.label}</span>
                  <input
                    className="absolute opacity-0 h-0 w-0"
                    type="radio"
                    checked={isChecked}
                    value={option.value}
                    name={name}
                    onChange={handleChange}
                  />
                  <span
                    className={`shrink-0 relative block h-[18px] w-[18px] rounded-full border-2 border-sleap-gray-950 dark:border-lightwhite after:content-[''] ${isChecked ? "after:block" : "after:hidden"} after:absolute after:w-[9px] after:h-[9px] after:bg-sleap-gray-950 dark:after:bg-lightwhite after:rounded-full after:top-50 after:top-1/2 after:left-1/2 after:-translate-x-1/2 after:-translate-y-1/2`}
                  />
                </label>
              </li>
            );
          })}
        </ul>
        {isFullScreenModal && (
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-lightwhite shadow-[0_-20px_25px_-5px_rgba(0,0,0,0.1)] dark:bg-sleap-gray-850">
            <Button fullWidth onClick={onConfirm}>
              {t("Other.confirm")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
